/**
 * Typesense API adapter v2 — SDK-based, disjunctive facets, related products.
 */

class TypesenseAPIAdapterV2 extends CollectionAdapterV2 {
  static SIZE_FACET_FIELD = 'size.facet_key';
  static SIZE_FILTER_VALUE_SEP = '::';

  static TYPESENSE_TO_CANONICAL_FILTER = {
    sub_category: 'product_type',
    'size.facet_key': 'size',
    'size.value_facet': 'size'
  };

  static CANONICAL_TO_TYPESENSE_FILTER = {
    product_type: 'sub_category',
    size: 'size.facet_key',
    brand: 'brand',
    gender: 'gender',
    category: 'category',
    colour: 'colour',
    condition: 'condition',
    price: 'price'
  };

  static FACET_BY_ALL = 'price,brand(sort_by: _alpha:asc),gender,category(sort_by: _alpha:asc),sub_category(sort_by: _alpha:asc),size.facet_key(sort_by: size.facet_sort:asc),colour(sort_by: _alpha:asc),condition(sort_by: condition_order:desc)';

  static FACET_BY_FIELD = {
    price: 'price',
    brand: 'brand(sort_by: _alpha:asc)',
    gender: 'gender',
    category: 'category(sort_by: _alpha:asc)',
    sub_category: 'sub_category(sort_by: _alpha:asc)',
    'size.facet_key': 'size.facet_key(sort_by: size.facet_sort:asc)',
    colour: 'colour(sort_by: _alpha:asc)',
    condition: 'condition(sort_by: condition_order:desc)'
  };

  static FACET_RETURN_PARENT = 'size.facet_key';

  static DISJUNCTIVE_FILTER_KEYS = [
    'brand', 'vendor', 'gender', 'category', 'product_type', 'size', 'colour', 'condition', 'price'
  ];

  constructor(config) {
    super(config);
    this.productsPerPage = config.productsPerPage || 20;
    this.client = this.createClient(config);
  }

  createClient(config) {
    if (typeof Typesense === 'undefined') {
      console.error('[TypesenseAPIV2] Typesense SDK not loaded');
      return null;
    }

    const endpoint = config.endpoint || '';
    const url = new URL(endpoint.startsWith('http') ? endpoint : `https://${endpoint}`);

    return new Typesense.Client({
      nodes: [{
        host: url.hostname,
        port: url.port ? parseInt(url.port, 10) : (url.protocol === 'https:' ? 443 : 80),
        protocol: url.protocol.replace(':', '')
      }],
      apiKey: config.apiKey,
      connectionTimeoutSeconds: 5
    });
  }

  getName() {
    return 'Typesense API v2';
  }

  static encodeSizeFilterValue(groupName, value) {
    if (!groupName) return value;
    return `${groupName}${TypesenseAPIAdapterV2.SIZE_FILTER_VALUE_SEP}${value}`;
  }

  static decodeSizeFilterValue(encoded) {
    const sep = TypesenseAPIAdapterV2.SIZE_FILTER_VALUE_SEP;
    const index = String(encoded).indexOf(sep);
    if (index === -1) return { groupName: null, value: encoded };
    return {
      groupName: encoded.slice(0, index),
      value: encoded.slice(index + sep.length)
    };
  }

  static escapeTypesenseFilterValue(value) {
    const str = String(value);
    if (/[\s,'`]/.test(str)) {
      return `\`${str.replace(/`/g, '\\`')}\``;
    }
    return str;
  }

  buildSizeFilterClause(encodedSize) {
    const { groupName } = TypesenseAPIAdapterV2.decodeSizeFilterValue(encodedSize);
    if (groupName) {
      const escapedKey = TypesenseAPIAdapterV2.escapeTypesenseFilterValue(encodedSize);
      return `size.facet_key:=[${escapedKey}]`;
    }

    const { value } = TypesenseAPIAdapterV2.decodeSizeFilterValue(encodedSize);
    const escapedValue = TypesenseAPIAdapterV2.escapeTypesenseFilterValue(value);
    return `size.value_facet:=[${escapedValue}]`;
  }

  getSortOptionsMapping({ context = 'collection' } = {}) {
    return {
      PRICE_DROP_RECENT: 'last_price_drop_at:desc',
      PRICE_DROP_BIGGEST: 'price_drop_amount:desc',
      PRICE_DROP_PERCENTAGE: 'price_drop_percentage:desc',
      LATEST_ACTIVITY_NEWEST: 'latest_activity_at:desc',
      ADDED_NEW_TO_OLD: 'created_at:desc',
      ADDED_OLD_TO_NEW: 'created_at:asc',
      PRICE_LOW_TO_HIGH: 'price:asc',
      PRICE_HIGH_TO_LOW: 'price:desc',
      MOST_FAVOURITED: 'favourites_count:desc'
    };
  }

  async getCollectionData(handle, filters, page, searchQuery) {
    try {
      if (!this.client || !this.config.apiKey || !this.config.collection) {
        return this.getEmptyState();
      }

      const requestMeta = this.extractRequestMeta(filters);
      const normalizedFilters = this.normalizeIncomingFilters(filters);
      const isSearch = !!searchQuery;
      const sortContext = isSearch ? 'search' : 'collection';
      const effectiveSortBy = normalizedFilters.sort_by || (!isSearch ? this.config.defaultSort : null);
      const effectiveCanonicalSort = this.toCanonicalSortId(effectiveSortBy, sortContext) || effectiveSortBy;
      const sortExpression = this.buildSortExpression(effectiveSortBy, sortContext);
      const isPreview = requestMeta.preview;
      const perPage = isPreview ? 0 : this.productsPerPage;

      const baseSearch = {
        collection: this.config.collection,
        q: searchQuery || '*',
        query_by: 'title,brand,category,sub_category,colour,size.value_facet,size.facet_key,size_group_value_product_card,condition,external_id',
        filter_by: this.buildFilterExpression(handle, normalizedFilters),
        facet_by: TypesenseAPIAdapterV2.FACET_BY_ALL,
        facet_return_parent: TypesenseAPIAdapterV2.FACET_RETURN_PARENT,
        max_facet_values: 250,
        page,
        per_page: perPage,
        use_cache: true
      };

      if (sortExpression) baseSearch.sort_by = sortExpression;

      const disjunctiveFields = this.getActiveDisjunctiveFields(normalizedFilters);
      const searches = [baseSearch];

      disjunctiveFields.forEach(fieldName => {
        const canonicalId = this.toCanonicalFilterId(fieldName);
        searches.push({
          collection: this.config.collection,
          q: searchQuery || '*',
          query_by: 'title,brand,category,sub_category,colour,size.value_facet,size.facet_key,size_group_value_product_card,condition,external_id',
          filter_by: this.buildFilterExpression(handle, normalizedFilters, { exclude: [canonicalId] }),
          facet_by: TypesenseAPIAdapterV2.FACET_BY_FIELD[fieldName],
          facet_return_parent: TypesenseAPIAdapterV2.FACET_RETURN_PARENT,
          max_facet_values: 250,
          page: 1,
          per_page: 0,
          use_cache: true
        });
      });

      const response = searches.length > 1
        ? await this.executeDisjunctiveSearch(searches)
        : await this.executeSearch(baseSearch);

      return this.transformResponse(response, handle, normalizedFilters, page, isSearch, searchQuery, {
        baselineFilters: requestMeta.baselineFilters,
        effectiveCanonicalSort,
        sortContext
      });
    } catch (error) {
      console.error('[TypesenseAPIV2] Error:', error);
      return this.getEmptyState();
    }
  }

  normalizeIncomingFilters(filters = {}) {
    const normalized = { ...filters };
    delete normalized.__preview;
    delete normalized.__baselineFilters;

    const productTypeValue = normalized.product_type ?? normalized.productType ?? normalized.sub_category;
    if (productTypeValue != null) {
      normalized.product_type = productTypeValue;
      delete normalized.productType;
      delete normalized.sub_category;
    }

    Object.keys(normalized).forEach(key => {
      const canonicalKey = normalizeFilterIdAlias(key);
      if (canonicalKey !== key && normalized[canonicalKey] == null) {
        normalized[canonicalKey] = normalized[key];
        delete normalized[key];
      }
    });

    if ((normalized.price_min != null || normalized.price_max != null) && !normalized.price) {
      normalized.price = { min: normalized.price_min, max: normalized.price_max };
      delete normalized.price_min;
      delete normalized.price_max;
    }

    return normalized;
  }

  extractRequestMeta(filters = {}) {
    return {
      preview: Boolean(filters.__preview),
      baselineFilters: filters.__baselineFilters || null
    };
  }

  toCanonicalFilterId(fieldName) {
    return TypesenseAPIAdapterV2.TYPESENSE_TO_CANONICAL_FILTER[fieldName]
      || normalizeFilterIdAlias(fieldName);
  }

  toTypesenseField(filterId) {
    return TypesenseAPIAdapterV2.CANONICAL_TO_TYPESENSE_FILTER[filterId] || filterId;
  }

  getActiveDisjunctiveFields(filters) {
    const activeFields = [];

    TypesenseAPIAdapterV2.DISJUNCTIVE_FILTER_KEYS.forEach(key => {
      if (key === 'price') {
        if (filters.price?.min != null || filters.price?.max != null) activeFields.push('price');
        return;
      }
      if (key === 'vendor') {
        if (filters.vendor && !filters.brand) activeFields.push('brand');
        return;
      }
      if (filters[key]) activeFields.push(this.toTypesenseField(key));
    });

    return [...new Set(activeFields)];
  }

  buildFilterExpression(handle, filters, { exclude = [] } = {}) {
    const excludeSet = new Set(exclude.map(filterId => this.toTypesenseField(filterId)));
    const filterParts = ['available_for_sale:=true'];

    if (handle && !filters.searchQuery) {
      filterParts.push(`collection_handles:=[${handle}]`);
    }

    if (!excludeSet.has('brand') && filters.brand) {
      const brands = Array.isArray(filters.brand) ? filters.brand : [filters.brand];
      filterParts.push(`brand:=[${brands.join(',')}]`);
    } else if (!excludeSet.has('brand') && filters.vendor) {
      const vendors = Array.isArray(filters.vendor) ? filters.vendor : [filters.vendor];
      filterParts.push(`brand:=[${vendors.join(',')}]`);
    }

    if (!excludeSet.has('gender') && filters.gender) {
      const genders = Array.isArray(filters.gender) ? filters.gender : [filters.gender];
      filterParts.push(`gender:=[${genders.join(',')}]`);
    }

    if (!excludeSet.has('category') && filters.category) {
      const categories = Array.isArray(filters.category) ? filters.category : [filters.category];
      filterParts.push(`category:=[${categories.join(',')}]`);
    }

    if (!excludeSet.has('sub_category') && filters.product_type) {
      const productTypes = Array.isArray(filters.product_type) ? filters.product_type : [filters.product_type];
      filterParts.push(`sub_category:=[${productTypes.join(',')}]`);
    }

    if (!excludeSet.has('size.facet_key') && filters.size) {
      const sizes = Array.isArray(filters.size) ? filters.size : [filters.size];
      const clauses = sizes.map(size => this.buildSizeFilterClause(size));
      filterParts.push(clauses.length === 1 ? clauses[0] : `(${clauses.join(' || ')})`);
    }

    if (!excludeSet.has('colour') && filters.colour) {
      const colours = Array.isArray(filters.colour) ? filters.colour : [filters.colour];
      filterParts.push(`colour:=[${colours.join(',')}]`);
    }

    if (!excludeSet.has('condition') && filters.condition) {
      const conditions = Array.isArray(filters.condition) ? filters.condition : [filters.condition];
      filterParts.push(`condition:=[${conditions.join(',')}]`);
    }

    if (!excludeSet.has('price') && filters.price) {
      const minPrice = filters.price.min != null ? Math.round(parseFloat(filters.price.min) * 100) : 0;
      const maxPrice = filters.price.max != null ? Math.round(parseFloat(filters.price.max) * 100) : 999999999;
      filterParts.push(`price:[${minPrice}..${maxPrice}]`);
    }

    return filterParts.join(' && ');
  }

  buildSortExpression(sortBy, context = 'collection') {
    if (!sortBy) return null;
    const mapping = this.getSortOptionsMapping({ context });
    if (mapping[sortBy]) return mapping[sortBy];
    if (sortBy.includes(':')) return sortBy;
    return null;
  }

  async executeSearch(params) {
    const { collection, ...searchParams } = params;
    const data = await this.client.collections(collection).documents().search(searchParams);
    if (!data.hits) data.hits = [];
    return data;
  }

  async executeDisjunctiveSearch(searches) {
    const data = await this.client.multiSearch.perform({ searches });
    const results = data.results || [];
    if (!results.length) throw new Error('Invalid multi_search response');

    const mainResult = { ...results[0] };
    if (!mainResult.hits) mainResult.hits = [];

    const disjunctiveFacetsByField = {};
    searches.slice(1).forEach((search, index) => {
      const facetField = (search.facet_by || '').split('(')[0];
      const facet = results[index + 1]?.facet_counts?.find(entry => entry.field_name === facetField)
        || results[index + 1]?.facet_counts?.[0];
      if (facetField && facet) disjunctiveFacetsByField[facetField] = facet;
    });

    mainResult.facet_counts = this.mergeDisjunctiveFacetCounts(
      mainResult.facet_counts || [],
      disjunctiveFacetsByField
    );

    return mainResult;
  }

  async getRelatedProducts({ subCategory, size, price, excludeHandle, limit = 16, sort = 'latest_activity_at:desc' }) {
    if (!this.client || !this.config.collection) return [];

    const filters = { product_type: [subCategory] };
    if (price != null && !Number.isNaN(price)) {
      filters.price = { min: price * 0.75, max: price * 1.5 };
    }

    const filterParts = [this.buildFilterExpression(null, filters)];
    if (size) filterParts.push(`size.value_facet:=[${size}]`);
    if (excludeHandle) filterParts.push(`handle:!=${excludeHandle}`);

    const response = await this.executeSearch({
      collection: this.config.collection,
      q: '*',
      query_by: 'title',
      filter_by: filterParts.join(' && '),
      sort_by: sort,
      per_page: limit,
      page: 1,
      use_cache: true
    });

    return (response.hits || []).map(hit => this.transformProduct(hit.document));
  }

  async getCollectionProducts({ handle, sortBy, limit = 7 }) {
    if (!this.client || !this.config.collection || !handle) return [];

    const filters = sortBy ? { sort_by: sortBy } : {};
    const sortExpression = this.buildSortExpression(sortBy, 'collection');
    const searchParams = {
      collection: this.config.collection,
      q: '*',
      query_by: 'title,brand,category,sub_category',
      filter_by: this.buildFilterExpression(handle, filters),
      per_page: limit,
      page: 1,
      use_cache: true
    };

    if (sortExpression) searchParams.sort_by = sortExpression;

    const response = await this.executeSearch(searchParams);
    return (response.hits || []).map(hit => this.transformProduct(hit.document));
  }

  transformResponse(response, handle, filters, page, isSearch, searchQuery, options = {}) {
    const { baselineFilters = null, effectiveCanonicalSort = null, sortContext = 'collection' } = options;
    const products = (response.hits || []).map(hit => this.transformProduct(hit.document));
    const transformedFilters = this.transformFacets(
      response.facet_counts || [],
      filters,
      handle || `search?q=${encodeURIComponent(searchQuery || '')}`
    );

    const priceFilter = this.buildPriceRangeFilter(response.facet_counts, filters);
    if (priceFilter) transformedFilters.push(priceFilter);

    const activeFilters = this.buildActiveFilters(
      filters,
      transformedFilters,
      handle || `search?q=${encodeURIComponent(searchQuery || '')}`
    );

    const activeSortBy = effectiveCanonicalSort
      || this.toCanonicalSortId(filters.sort_by, sortContext)
      || filters.sort_by;
    const sortOptions = this.buildSortOptions(activeSortBy, sortContext);
    const totalResults = response.found || 0;
    const totalPages = Math.ceil(totalResults / this.productsPerPage);

    return this.buildCollectionResult({
      products,
      filters: transformedFilters,
      active_filters: activeFilters,
      sort_options: sortOptions,
      products_count_total: response.found,
      pagination: {
        current_page: page,
        total_pages: totalPages,
        per_page: this.productsPerPage,
        has_next: page < totalPages,
        has_previous: page > 1,
        next_url: null,
        previous_url: null
      },
      current_url: isSearch ? `/search?q=${encodeURIComponent(searchQuery)}` : `/collections/${handle}`,
      baselineFilters,
      activeFiltersForCatalog: filters,
      currentSort: activeSortBy,
      sortContext,
      search_query: isSearch ? searchQuery : undefined
    });
  }

  transformProduct(doc) {
    return {
      id: doc.id,
      shopifyProductId: doc.shopify_product_id,
      handle: doc.handle,
      title: doc.title,
      vendor: doc.brand || '',
      price: {
        amount: (doc.price / 100).toFixed(2),
        currencyCode: doc.currency || 'GBP'
      },
      compareAtPrice: doc.compare_at_price ? {
        amount: (doc.compare_at_price / 100).toFixed(2),
        currencyCode: doc.currency || 'GBP'
      } : null,
      availableForSale: doc.available_for_sale,
      featuredImage: {
        url: doc.featured_image_url || '',
        altText: doc.featured_image_alt || doc.title,
        width: 800,
        height: 800
      },
      tags: [],
      createdAt: new Date(doc.created_at * 1000).toISOString(),
      url: doc.url,
      sizeData: doc.size_group_value_product_card
        ? { value: doc.size_group_value_product_card }
        : null,
      condition: doc.condition ? { value: doc.condition } : null,
      favouritesCount: doc.favourites_count ?? 0
    };
  }

  transformFacets(facets, activeFilters, handle) {
    return facets
      .filter(facet => facet.field_name !== 'price')
      .filter(facet => facet.field_name !== 'size.value_facet')
      .map(facet => {
        const filterId = this.toCanonicalFilterId(facet.field_name);
        if (filterId === 'size') {
          return this.transformSizeFacet(facet, activeFilters, handle);
        }

        return {
          id: filterId,
          label: formatCanonicalFilterLabel(filterId),
          type: 'option',
          values: (facet.counts || []).map(count => ({
            value: count.value,
            label: count.value,
            count: count.count,
            active: this.isFilterValueActive(filterId, count.value, activeFilters),
            url: this.buildFilterUrl(handle, filterId, count.value, activeFilters)
          }))
        };
      });
  }

  transformSizeFacet(facet, activeFilters, handle) {
    const groupsByName = new Map();
    const ungroupedByValue = new Map();

    (facet.counts || []).forEach(count => {
      const parent = count.parent || {};
      const decoded = TypesenseAPIAdapterV2.decodeSizeFilterValue(count.value);
      const groupName = parent.group_name || decoded.groupName || '';
      const groupOrder = parent.group_order ?? 999;
      const displayLabel = parent.value_facet || decoded.value || count.value;
      const filterValue = decoded.groupName
        ? count.value
        : TypesenseAPIAdapterV2.encodeSizeFilterValue(groupName, displayLabel);
      const entry = {
        value: filterValue,
        label: displayLabel,
        groupName: groupName || undefined,
        count: count.count,
        active: this.isFilterValueActive('size', filterValue, activeFilters),
        url: this.buildFilterUrl(handle, 'size', filterValue, activeFilters)
      };

      if (!groupName) {
        if (!ungroupedByValue.has(filterValue)) {
          ungroupedByValue.set(filterValue, entry);
        }
        return;
      }

      if (!groupsByName.has(groupName)) {
        groupsByName.set(groupName, {
          name: groupName,
          order: groupOrder,
          values: [],
          valueKeys: new Set()
        });
      }

      const group = groupsByName.get(groupName);
      if (group.valueKeys.has(filterValue)) return;
      group.valueKeys.add(filterValue);
      group.values.push(entry);
    });

    const groups = [...groupsByName.values()]
      .map(({ valueKeys, ...group }) => group)
      .sort((a, b) => a.order - b.order);
    const ungroupedValues = [...ungroupedByValue.values()];

    if (groups.length && ungroupedValues.length) {
      groups.push({ name: 'Other', order: 998, values: ungroupedValues });
    }

    const hasGroups = groupsByName.size > 0;
    const flatValues = hasGroups ? groups.flatMap(group => group.values) : ungroupedValues;

    return {
      id: 'size',
      label: formatCanonicalFilterLabel('size'),
      type: hasGroups ? 'grouped_option' : 'option',
      groups: hasGroups ? groups : undefined,
      values: flatValues
    };
  }

  buildPriceRangeFilter(facetCounts, activeFilters) {
    const priceFacet = (facetCounts || []).find(facet => facet.field_name === 'price');
    const priceStats = priceFacet?.stats;
    if (!priceStats || priceStats.min == null || priceStats.max == null) return null;

    let min = Math.floor(priceStats.min / 100);
    let max = Math.ceil(priceStats.max / 100);
    if (min >= max) max = min + 1;

    return {
      id: 'price',
      label: 'Price',
      type: 'price_range',
      min,
      max,
      min_value: { amount: min },
      max_value: { amount: max },
      active_min: activeFilters.price?.min != null ? parseFloat(activeFilters.price.min) : min,
      active_max: activeFilters.price?.max != null ? parseFloat(activeFilters.price.max) : max
    };
  }

  isFilterValueActive(filterId, value, activeFilters) {
    const filterValues = activeFilters[filterId];
    if (!filterValues) return false;
    if (Array.isArray(filterValues)) return filterValues.includes(value);
    return filterValues === value;
  }

  buildFilterUrl(handle, filterId, value) {
    const isSearch = handle.includes('search?q=');
    const params = new URLSearchParams();
    if (isSearch) {
      const searchMatch = handle.match(/q=([^&]+)/);
      params.set('q', searchMatch ? decodeURIComponent(searchMatch[1]) : '');
    }
    params.set(`filter.${filterId}`, value);
    return isSearch ? `/search?${params.toString()}` : `/collections/${handle}?${params.toString()}`;
  }

  buildActiveFilters(filters, availableFilters, handle) {
    const active = [];
    Object.entries(filters).forEach(([filterId, filterValues]) => {
      if (filterId === 'sort_by' || filterId === 'searchQuery') return;
      const filter = availableFilters.find(f => f.id === filterId);
      if (!filter) return;

      if (Array.isArray(filterValues)) {
        filterValues.forEach(value => {
          const filterValue = filter.values?.find(v => v.value === value);
          if (filterValue) {
            let activeLabel = `${filter.label}: ${filterValue.label}`;
            if (filterId === 'size') {
              const groupName = filterValue.groupName
                || TypesenseAPIAdapterV2.decodeSizeFilterValue(value).groupName;
              if (groupName) {
                activeLabel = `${filter.label}: ${filterValue.label} (${groupName})`;
              }
            }
            active.push({
              filter_id: filterId,
              value,
              label: activeLabel,
              remove_url: this.buildFilterUrl(handle, filterId, value)
            });
          }
        });
      } else if (typeof filterValues === 'object' && filterValues.min !== undefined) {
        active.push({
          filter_id: filterId,
          value: `${filterValues.min}-${filterValues.max}`,
          label: `${filter.label}: £${filterValues.min} - £${filterValues.max}`,
          remove_url: this.buildFilterUrl(handle, filterId, null)
        });
      }
    });
    return active;
  }
}

if (typeof window !== 'undefined') {
  window.TypesenseAPIAdapterV2 = TypesenseAPIAdapterV2;
}
